import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useHead } from '@unhead/vue'
import {
  absoluteUrl,
  buildPageTitle,
  DEFAULT_DESCRIPTION_AR,
  DEFAULT_DESCRIPTION_EN,
  DEFAULT_KEYWORDS,
  hreflangLinks,
  ogLocaleTags,
  resolveOgImage,
  SITE_NAME,
} from '@/utils/seo'

type SeoMeta = {
  titleKey?: string
  title?: string
  descriptionKey?: string
  description?: string
  noIndex?: boolean
}

/**
 * Applies default SEO from route.meta.seo for list/static routes.
 * Detail views set meta.seoManaged and call usePageSeo with entity names.
 */
export function useRouteSeo() {
  const route = useRoute()
  const { t, locale } = useI18n()

  useHead(computed(() => {
    if (route.meta.seoManaged) {
      // Detail views own the document head; do not override.
      return {}
    }

    const seo = (route.meta.seo || {}) as SeoMeta
    const pageTitle = seo.title
      || (seo.titleKey ? t(seo.titleKey) : '')
    const description = seo.description
      || (seo.descriptionKey ? t(seo.descriptionKey) : '')
      || (locale.value === 'ar' ? DEFAULT_DESCRIPTION_AR : DEFAULT_DESCRIPTION_EN)
    const title = buildPageTitle(pageTitle, locale.value)
    const url = absoluteUrl(route.path)
    const image = resolveOgImage()
    const robots = seo.noIndex ? 'noindex, nofollow' : 'index, follow'

    return {
      title,
      htmlAttrs: {
        lang: locale.value === 'ar' ? 'ar' : 'en',
        dir: locale.value === 'ar' ? 'rtl' : 'ltr',
      },
      meta: [
        { name: 'description', content: description },
        { name: 'keywords', content: DEFAULT_KEYWORDS },
        { name: 'robots', content: robots },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: url },
        { property: 'og:image', content: image },
        { property: 'og:type', content: 'website' },
        { property: 'og:site_name', content: SITE_NAME },
        ...ogLocaleTags(locale.value),
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: image },
      ],
      // `as any`: unhead Link union treats rel:"alternate" as feed-like (requires type)
      link: [
        { rel: 'canonical', href: url },
        ...hreflangLinks(route.path),
      ] as any,
    }
  }))
}
